import {
  BellOutlined,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Input,
  List,
  message,
  Popconfirm,
  Space,
  Switch,
  Tag,
  Typography,
} from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PERMISSIONS } from "../../../constants/permissions";
import { usePermissions } from "../../../hooks/usePermissions";
import { sellerApi } from "../../../services/api";
import { RootState } from "../../../store";
import type { SellerInfo } from "../../../types/seller.types";

const SellerListPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [sellers, setSellers] = useState<SellerInfo[]>([]);
  const [dueNotifTogglingIds, setDueNotifTogglingIds] = useState<Set<number>>(new Set());
  const [searchText, setSearchText] = useState("");
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { user } = useSelector((state: RootState) => state.auth);
  const { isMobileDevice } = useSelector((state: RootState) => state.ui);

  const canAddSeller = hasPermission(PERMISSIONS.ADD_SELLER.name);
  const canUpdateSeller = hasPermission(PERMISSIONS.UPDATE_SELLER.name);
  const canDeleteSeller = hasPermission(PERMISSIONS.DELETE_SELLER.name);
  const canSellToken = hasPermission(PERMISSIONS.SELL_TOKEN.name);
  const canManageSellers = hasPermission(PERMISSIONS.MANAGE_SELLERS.name);

  const isAdmin = user?.designation?.toUpperCase().includes("ADMIN");
  const isDistributor = user?.designation
    ?.toUpperCase()
    .includes("DISTRIBUTOR");
  const isDealer = user?.designation?.toUpperCase().includes("DEALER");

  useEffect(() => {
    fetchSellers();
  }, []);

  const fetchSellers = async () => {
    try {
      if (loading) return;
      setLoading(true);
      let response;
      response = await sellerApi.getSellers();
      if (response.status === "SUCCESS") {
        setSellers(response.data);
      }
    } catch (error) {
      message.error("Failed to fetch partners");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDuePaymentNotification = async (seller: SellerInfo, checked: boolean) => {
    setDueNotifTogglingIds((prev) => new Set(prev).add(seller.id));
    try {
      const response = await sellerApi.toggleDuePaymentNotification(seller.id, checked);
      if (response.status === "SUCCESS") {
        message.success(response.message || "Due payment notification updated");
        setSellers((prev) =>
          prev.map((s) => s.id === seller.id ? { ...s, duePaymentNotificationEnabled: checked } : s)
        );
      } else {
        message.error(response.message || "Failed to update due payment notification");
      }
    } catch (error) {
      message.error("Failed to update due payment notification");
    } finally {
      setDueNotifTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(seller.id);
        return next;
      });
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      const response = await sellerApi.delete(id.toString(), "DISTRIBUTOR");
      if (response.status === "SUCCESS") {
        message.success("Sales partner deleted successfully");
        fetchSellers();
      } else {
        message.error(response.message || "Failed to delete partner");
      }
    } catch (error) {
      message.error("Failed to delete partner");
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on search term
  const getFilteredData = () => {
    if (!searchText) return sellers;

    return sellers.filter((partner) => {
      const searchFields = [
        partner.name,
        partner.phone,
        partner.organization,
        partner.designation,
        partner.tokenBalance?.toString(),
      ].map((field) => field?.toLowerCase() || "");

      const searchLower = searchText.toLowerCase();

      return searchFields.some((field) => field.includes(searchLower));
    });
  };

  const { Title, Text } = Typography;

  return (
    <Card
      title="Sales Partners"
      extra={
        <div
          style={{
            display: "flex",
            gap: "8px",
            width: "100%",
            alignItems: "center",
          }}
        >
          <Input
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchOutlined />}
            allowClear
            style={{ flex: "1 1 auto", minWidth: "120px" }}
          />
          {canAddSeller && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(PERMISSIONS.ADD_SELLER.route)}
              style={{ flexShrink: 0, whiteSpace: "nowrap" }}
            >
              {isMobileDevice ? "" : "Add Partner"}
            </Button>
          )}
        </div>
      }
    >
      <List
        itemLayout="horizontal"
        size={"large"}
        dataSource={getFilteredData()}
        loading={loading}
        pagination={{
          pageSize: 10,
          responsive: true,
          showSizeChanger: false,
        }}
        renderItem={(seller) => (
          <List.Item
            key={`${seller.id}-${seller.name}`}
            style={{
              padding: '8px 10px',
              marginBottom: '6px',
              backgroundColor: '#fff',
              borderRadius: '6px',
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.04)',
              borderLeft: (seller.totalDue ?? 0) > 0 ? '3px solid #ff4d4f' : '3px solid transparent'
            }}
            actions={
              [
                <div
                  key="actions"
                  style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: "4px", width: "100%" }}
                >
                  {canUpdateSeller && (
                    <Button
                      type="link"
                      icon={<EditOutlined />}
                      onClick={() =>
                        navigate(PERMISSIONS.ADD_SELLER.route, {
                          state: { id: seller.id },
                        })
                      }
                      title="Edit"
                    >
                      {!isMobileDevice && "Edit"}
                    </Button>
                  )}
                  {canManageSellers && (
                    <Button
                      type="link"
                      icon={<SettingOutlined />}
                      onClick={() =>
                        navigate(PERMISSIONS.MANAGE_SELLERS.route, {
                          state: { data: seller },
                        })
                      }
                      title="Manage"
                    >
                      {!isMobileDevice && "Manage"}
                    </Button>
                  )}
                  {canSellToken && (
                    <Button
                      type="link"
                      icon={<DollarOutlined />}
                      onClick={() =>
                        navigate("/dashboard/sales/sell-token", {
                          state: {
                            sellerId: seller.id,
                            sellerName: seller.name,
                            sellerPhone: seller.phone,
                          },
                        })
                      }
                      title="Sell Token"
                    >
                      {!isMobileDevice && "Sell Token"}
                    </Button>
                  )}
                  {canDeleteSeller && (
                    <Popconfirm
                      title="Are you sure you want to delete this partner?"
                      onConfirm={() => handleDelete(seller.id)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        title="Delete"
                      >
                        {!isMobileDevice && "Delete"}
                      </Button>
                    </Popconfirm>
                  )}
                </div>,
              ].filter(Boolean) as React.ReactNode[]
            }
          >
            <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", minWidth: 0 }}>
                <Text strong style={{ fontSize: 14, lineHeight: 1.3 }}>{seller.name}</Text>
                {seller.designation && (
                  <Tag color="blue" style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: "16px", padding: "0 6px" }}>
                    {seller.designation}
                  </Tag>
                )}
                <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {seller.tokenBalance?.toLocaleString() ?? "0"} tk
                  </Text>
                  {(seller.totalDue ?? 0) > 0 && (
                    <Text style={{ fontSize: 12, color: "#ff4d4f", fontWeight: 600 }}>
                      ৳{seller.totalDue!.toLocaleString()} due
                    </Text>
                  )}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, color: "rgba(0,0,0,0.55)", fontSize: 12, lineHeight: 1.3, minWidth: 0 }}>
                <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{seller.phone || "—"}</Text>
                {seller.organization && (
                  <Text type="secondary" style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    · {seller.organization}
                  </Text>
                )}
                {canManageSellers && (
                  <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                    <BellOutlined style={{ fontSize: 11, color: "rgba(0,0,0,0.45)" }} />
                    <Switch
                      size="small"
                      checked={seller.duePaymentNotificationEnabled ?? true}
                      loading={dueNotifTogglingIds.has(seller.id)}
                      disabled={dueNotifTogglingIds.has(seller.id)}
                      onChange={(checked) => handleToggleDuePaymentNotification(seller, checked)}
                    />
                  </span>
                )}
              </div>
            </div>
          </List.Item>
        )}
      />
    </Card>
  );
};

export default SellerListPage;
